import { z } from 'zod';
import { uuidSchema } from './common.js';
export function isValidTimeZone(value) {
    try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return value.length > 0;
    }
    catch {
        return false;
    }
}
const currencies = new Set(Intl.supportedValuesOf('currency'));
export const timezoneSchema = z.string().refine(isValidTimeZone, 'Unknown IANA time zone');
export const currencySchema = z
    .string()
    .trim()
    .toUpperCase()
    .refine((code) => currencies.has(code), 'Unknown ISO 4217 currency code');
export const countrySchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Use a 2-letter ISO 3166 country code');
export const languageSchema = z
    .string()
    .trim()
    .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, 'Use a BCP 47 language tag, e.g. "en" or "en-IN"');
export const codeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{0,19}$/, 'Code: 1-20 characters A-Z, 0-9, "_" or "-"');
const text = (max) => z.string().trim().max(max);
const optionalText = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
export const slugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Slug: lowercase letters, digits and single hyphens');
// ---- organizations ----
export const createOrganizationSchema = z.object({
    name: text(200).min(1),
    slug: slugSchema.max(60),
    timezone: timezoneSchema.default('UTC'),
    currencyCode: currencySchema.default('USD'),
});
export const updateOrganizationSchema = z
    .object({
    name: text(200).min(1),
    timezone: timezoneSchema,
    currencyCode: currencySchema,
    isActive: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
// ---- schools ----
const address = {
    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(100),
    state: optionalText(100),
    postalCode: optionalText(20),
    phone: optionalText(30),
    email: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
};
export const createSchoolSchema = z.object({
    name: text(200).min(1),
    code: codeSchema,
    ...address,
    countryCode: countrySchema.nullable().optional(),
    website: z.string().trim().url().max(300).nullable().optional(),
    /** Defaults to the organization's value when omitted. */
    timezone: timezoneSchema.optional(),
    currencyCode: currencySchema.optional(),
    language: languageSchema.default('en'),
    organizationId: uuidSchema.optional(),
});
export const updateSchoolSchema = z
    .object({
    name: text(200).min(1),
    code: codeSchema,
    ...address,
    countryCode: countrySchema.nullable(),
    website: z.string().trim().url().max(300).nullable(),
    timezone: timezoneSchema,
    currencyCode: currencySchema,
    language: languageSchema,
    isActive: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
// ---- campuses ----
export const createCampusSchema = z.object({
    name: text(200).min(1),
    code: codeSchema,
    ...address,
    isMain: z.boolean().default(false),
});
export const updateCampusSchema = z
    .object({
    name: text(200).min(1),
    code: codeSchema,
    ...address,
    isMain: z.boolean(),
    isActive: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
// ---- academic years ----
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) &&
    new Date(`${v}T00:00:00Z`).toISOString().startsWith(v), 'Invalid date');
export const createAcademicYearSchema = z
    .object({
    name: text(50).min(1),
    startDate: isoDate,
    endDate: isoDate,
    isCurrent: z.boolean().default(false),
})
    .refine((v) => v.endDate > v.startDate, {
    message: 'End date must be after start date',
    path: ['endDate'],
});
export const updateAcademicYearSchema = z
    .object({
    name: text(50).min(1),
    startDate: isoDate,
    endDate: isoDate,
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required')
    .refine((v) => !v.startDate || !v.endDate || v.endDate > v.startDate, {
    message: 'End date must be after start date',
    path: ['endDate'],
});
//# sourceMappingURL=org.js.map